// 攝影機頁分組 tab bar + 分組管理對話框(CAM-24 Stage 3c,web)。
// 對齊 Android `DeviceGroupTabBar`/`ManageGroupsDialog`/`NewGroupDialog` 與 iOS
// `DeviceListView` chip bar:`[全部] [分組…] [＋] [管理]`。
// - 「全部」恆最左、預設選中,顯示全部裝置(未分組裝置只在此出現)。
// - ＋ 建立新分組;達 MAX_DEVICE_GROUPS(10)時整顆隱藏(對齊三端,非 disable)。
// - 管理:改名 / 刪除 / 上下重排(重排寫 device_group_order)。分組非空才顯示。

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFocusTrap } from '../../lib/useFocusTrap';

export function GroupTabBar({
  groups,
  selected,
  onSelect,
  onAdd,
  onManage,
}: {
  groups: string[];
  selected: string | null;
  onSelect: (group: string | null) => void;
  /** 未達上限時提供;達上限傳 undefined → ＋ 隱藏(對齊 Android/iOS)。 */
  onAdd?: () => void;
  /** 有分組時提供;無分組傳 undefined → 管理鈕隱藏。 */
  onManage?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="dv-group-tabs" data-testid="group-tabs">
      {/* ls-loop（MW、WCAG）：tablist 只能放 tab → 新增 / 管理兩顆移到外面；display: contents 版面不變 */}
      <div role="tablist" aria-label={t('devices.groupTabsLabel')} style={{ display: 'contents' }}>
      <button
        type="button"
        role="tab"
        aria-selected={selected === null}
        className={`dv-group-tab${selected === null ? ' dv-group-tab-on' : ''}`}
        data-testid="group-tab-all"
        onClick={() => onSelect(null)}
      >
        {t('devices.groupAll')}
      </button>
      {groups.map((g) => (
        <button
          key={g}
          type="button"
          role="tab"
          aria-selected={selected === g}
          className={`dv-group-tab${selected === g ? ' dv-group-tab-on' : ''}`}
          data-testid={`group-tab-${g}`}
          onClick={() => onSelect(g)}
        >
          {g}
        </button>
      ))}
      </div>
      {onAdd && (
        <button type="button" className="dv-group-tab dv-group-add" data-testid="group-tab-add" aria-label={t('devices.groupNew')} onClick={onAdd}>
          <span aria-hidden="true">＋</span>
        </button>
      )}
      {onManage && (
        <button type="button" className="dv-group-tab dv-group-manage" data-testid="group-manage" onClick={onManage}>
          {t('devices.groupManage')}
        </button>
      )}
    </div>
  );
}

/** 簡易置中 modal 殼(焦點陷阱 + Esc 取消 + 遮罩點擊取消)。 */
function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useFocusTrap(true, ref);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'grid', placeItems: 'center', zIndex: 1000, padding: 'var(--spacing-8)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="card" style={{ width: 'min(420px, 100%)', display: 'grid', gap: 'var(--spacing-5)' }}>
        <h3 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>{title}</h3>
        {children}
      </div>
    </div>
  );
}

/** 新建分組:名稱輸入 + 去重/長度驗證(對齊 Android NewGroupDialog;max 32,`分組已存在`)。 */
export function NewGroupDialog({ existing, onCreate, onClose }: { existing: string[]; onCreate: (name: string) => void; onClose: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const trimmed = name.trim();
  const dup = trimmed.length > 0 && existing.includes(trimmed);
  const canCreate = trimmed.length > 0 && !dup;
  return (
    <ModalShell title={t('devices.groupNew')} onClose={onClose}>
      <label style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
        <input
          data-autofocus
          value={name}
          maxLength={32}
          placeholder={t('devices.groupNameHint')}
          data-testid="group-new-input"
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canCreate) onCreate(trimmed);
          }}
        />
      </label>
      {dup && <p className="error-text" style={{ margin: 0 }}>{t('devices.groupDuplicate')}</p>}
      <div style={{ display: 'flex', gap: 'var(--spacing-4)', justifyContent: 'flex-end' }}>
        <button className="btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
        <button className="btn-primary" disabled={!canCreate} data-testid="group-new-create" onClick={() => onCreate(trimmed)}>
          {t('devices.groupNew')}
        </button>
      </div>
    </ModalShell>
  );
}

/** 管理分組:每組一列,↑↓ 重排 / 改名 / 刪除;完成關閉。改名開子對話框。 */
export function ManageGroupsDialog({
  groups,
  onMove,
  onRename,
  onDelete,
  onClose,
}: {
  groups: string[];
  onMove: (index: number, delta: -1 | 1) => void;
  onRename: (oldName: string, newName: string) => void;
  onDelete: (group: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [renaming, setRenaming] = useState<string | null>(null);
  return (
    <ModalShell title={t('devices.groupManage')} onClose={onClose}>
      <ul className="dv-group-manage-list" data-testid="group-manage-list">
        {groups.map((g, i) => (
          <li key={g} className="dv-group-manage-row" data-testid={`group-manage-row-${g}`}>
            <span className="dv-group-manage-name">{g}</span>
            <div className="dv-group-manage-actions">
              <button className="btn-ghost" aria-label={t('common.moveUp')} disabled={i === 0} data-testid={`group-up-${g}`} onClick={() => onMove(i, -1)}>↑</button>
              <button className="btn-ghost" aria-label={t('common.moveDown')} disabled={i === groups.length - 1} data-testid={`group-down-${g}`} onClick={() => onMove(i, 1)}>↓</button>
              <button className="btn-ghost" data-testid={`group-rename-${g}`} onClick={() => setRenaming(g)}>{t('devices.groupRename')}</button>
              <button className="btn-danger" data-testid={`group-delete-${g}`} onClick={() => onDelete(g)}>{t('devices.groupDelete')}</button>
            </div>
          </li>
        ))}
      </ul>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn-primary" data-testid="group-manage-done" onClick={onClose}>{t('common.done')}</button>
      </div>
      {renaming !== null && (
        <RenameGroupDialog
          oldName={renaming}
          existing={groups}
          onRename={(newName) => {
            onRename(renaming, newName);
            setRenaming(null);
          }}
          onClose={() => setRenaming(null)}
        />
      )}
    </ModalShell>
  );
}

function RenameGroupDialog({ oldName, existing, onRename, onClose }: { oldName: string; existing: string[]; onRename: (newName: string) => void; onClose: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState(oldName);
  const trimmed = name.trim();
  const dup = trimmed.length > 0 && trimmed !== oldName && existing.includes(trimmed);
  const canRename = trimmed.length > 0 && trimmed !== oldName && !dup;
  return (
    <ModalShell title={t('devices.groupRenameTo', { name: oldName })} onClose={onClose}>
      <label style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
        <input
          data-autofocus
          value={name}
          maxLength={32}
          placeholder={t('devices.groupNameHint')}
          data-testid="group-rename-input"
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canRename) onRename(trimmed);
          }}
        />
      </label>
      {dup && <p className="error-text" style={{ margin: 0 }}>{t('devices.groupDuplicate')}</p>}
      <div style={{ display: 'flex', gap: 'var(--spacing-4)', justifyContent: 'flex-end' }}>
        <button className="btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
        <button className="btn-primary" disabled={!canRename} data-testid="group-rename-confirm" onClick={() => onRename(trimmed)}>
          {t('devices.groupRename')}
        </button>
      </div>
    </ModalShell>
  );
}
